import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useReactiveVar } from '@apollo/client';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import * as Device from 'react-device-detect';

import {
  useGetSpotBookingLazyQuery,
  useGetConsumerBookingLazyQuery,
  GetSpotBookingQuery,
  GetConsumerBookingQuery,
} from '@graphql';
import { useSession, useToastError, useStatusBarScrollToTop } from '@hooks';
import { previewBookingVar } from '@context';
import BookingHistory from './BookingHistory';
import BookingSpot from './BookingSpot';
import BookingConsumer from './BookingConsumer';
import EditConsumerForm from './booking-forms/EditConsumerForm';
import EditSpotForm from './booking-forms/EditSpotForm';
import BookingHeader from './BookingHeader';

interface BookingDialogProps {
  Trigger?: React.ElementType;
}

export function BookingDialog(props: Readonly<BookingDialogProps>) {
  const { Trigger } = props;
  const [open, setOpen] = React.useState(false);
  const { account } = useSession();
  const { t } = useTranslation('cta');
  const navigate = useNavigate();
  const toastError = useToastError();
  const { bookingId: routeBookingId } = useParams<{ bookingId?: string }>();
  const previewBookingId = useReactiveVar(previewBookingVar);
  const bookingId = routeBookingId ?? previewBookingId;
  const location = useLocation();

  // // Handle iOS status bar tap to scroll to top
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  useStatusBarScrollToTop(scrollRef);

  const [getConsumerBooking, { data: consumer, loading: consumerLoading }] = useGetConsumerBookingLazyQuery({
    onError() {
      toastError({ message: t('toast.error.Unable to found the booking', { ns: 'booking' }) });
      handleClose();
    },
  });

  const [getSpotBooking, { data: spot, loading: spotLoading }] = useGetSpotBookingLazyQuery({
    onError() {
      toastError({ message: t('toast.error.Unable to found the booking', { ns: 'booking' }) });
      handleClose();
    },
  });

  React.useEffect(() => {
    if (bookingId) {
      handleOpen();
      if (account?.__typename === 'Consumer') {
        getConsumerBooking({ variables: { id: bookingId } });
      } else if (account?.__typename === 'Spot') {
        getSpotBooking({ variables: { id: bookingId } });
      }
    }
  }, [bookingId]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = (toRoot = false) => {
    const from = location.search?.length ? `${location.pathname}?${location.search}` : location.pathname;
    const navigateOptions = { preventScrollReset: true, state: { from } };

    let prevPath = '';

    // prevent user to quit app if he went to the dialog from a direct link
    if (toRoot || window.history.state.idx <= 1) {
      prevPath = account?.__typename === 'Spot' ? '/me/calendar/booking' : '/me/calendar';
    } else {
      prevPath = location.state?.from ?? -1;
    }

    previewBookingVar(null);
    setOpen(false);
    navigate(prevPath, navigateOptions);
  };

  return (
    <>
      {Trigger && (
        <Box onClick={handleOpen} p={0} m={0}>
          <Trigger />
        </Box>
      )}
      <Dialog
        open={open}
        onClose={() => handleClose()}
        fullScreen={Device.isMobileOnly}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        slotProps={{
          paper: {
            ref: scrollRef,
            sx: {
              height: '100%',
              pb: 0,
            },
          },
        }}
      >
        <BookingDialogContent
          bookingId={bookingId}
          isLoading={consumerLoading || spotLoading}
          consumerBooking={consumer?.booking}
          spotBooking={spot?.booking}
          handleClose={handleClose}
        />
      </Dialog>
    </>
  );
}

interface BookingDialogContentProps {
  bookingId: string | null;
  isLoading: boolean;
  consumerBooking?: GetConsumerBookingQuery['booking'];
  spotBooking?: GetSpotBookingQuery['booking'];
  handleClose: () => void;
}

function BookingDialogContent(props: Readonly<BookingDialogContentProps>) {
  const { bookingId, isLoading, consumerBooking, spotBooking, handleClose } = props;
  const [isEdit, setIsEdit] = React.useState(false);
  const { account } = useSession();
  const { t } = useTranslation('cta');
  const location = useLocation();
  const navigate = useNavigate();

  const value = location.pathname.endsWith('/history') ? 'history' : 'detail';

  const paddingTop = Device.isDesktop ? 2 : 10;

  const panelSx = {
    p: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const nextPath = location.pathname.replace(/\/(detail|history)$/, `/${newValue}`);

    navigate(nextPath, {
      preventScrollReset: true,
      state: {
        replace: true,
        from: location.state?.from,
      },
    });
  };

  const handleEdit = () => {
    setIsEdit(!isEdit);
  };

  if (!bookingId) {
    return null;
  }

  if (isLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress color="primary" />
      </Stack>
    );
  }

  if (isEdit) {
    if (account?.__typename === 'Consumer' && consumerBooking) {
      return (
        <>
          <DialogTitle sx={{ p: 0 }}>
            <BookingHeader handleBack={handleEdit}>
              <Typography variant="body2" fontWeight={500}>
                {t('header.booking at', { ns: 'booking', spot: consumerBooking.spot.name })}
              </Typography>
            </BookingHeader>
          </DialogTitle>
          <DialogContent sx={{ px: 2, mt: paddingTop }}>
            <EditConsumerForm booking={consumerBooking} handleEdit={handleEdit} />
          </DialogContent>
        </>
      );
    } else if (account?.__typename === 'Spot' && spotBooking) {
      return (
        <>
          <DialogTitle sx={{ p: 0 }}>
            <BookingHeader handleBack={handleEdit}>
              <Typography variant="body2" fontWeight={500}>
                {t('header.booking at', { ns: 'booking', spot: spotBooking.spot.name })}
              </Typography>
            </BookingHeader>
          </DialogTitle>
          <DialogContent sx={{ px: 2, mt: paddingTop }}>
            <EditSpotForm booking={spotBooking} handleEdit={handleEdit} />
          </DialogContent>
        </>
      );
    }
  }

  return (
    <TabContext value={value}>
      <BookingHeader handleBack={handleClose}>
        <TabList
          onChange={handleChange}
          aria-label="booking tabs"
          textColor="inherit"
          TabIndicatorProps={{
            sx: {
              backgroundColor: '#b2b2b2',
            },
          }}
        >
          <Tab label={t('title', { ns: 'booking' })} value="detail" />
          <Tab label={t('history.title', { ns: 'booking' })} value="history" />
        </TabList>
      </BookingHeader>

      <TabPanel value="detail" sx={panelSx}>
        {consumerBooking && <BookingConsumer booking={consumerBooking} handleEdit={handleEdit} />}
        {spotBooking && <BookingSpot booking={spotBooking} handleEdit={handleEdit} />}
      </TabPanel>
      <TabPanel value="history" sx={panelSx}>
        <BookingHistory
          bookingId={bookingId}
          updates={consumerBooking?.updates || spotBooking?.updates || []}
          useNote={spotBooking?.client.__typename === 'BookingClient'}
        />
      </TabPanel>
    </TabContext>
  );
}
